import {
  FieldValues,
  DeepMap,
  FieldError,
  Controller,
  Control,
} from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

function toLuxonPickerValue(value: unknown): DateTime | null {
  if (value == null || value === '') return null;
  if (value instanceof DateTime) return value.isValid ? value : null;
  if (typeof value === 'string') {
    const parsed = DateTime.fromISO(value);
    return parsed.isValid ? parsed : null;
  }
  return null;
}

interface DateInputProps {
  label: string;
  name: string;
  control: Control;
  className?: string;
  required?: boolean;
  errors?: DeepMap<FieldValues, FieldError>;
  defaultValue?: any;
  minDate?: DateTime;
  maxDate?: DateTime;
  disabled?: boolean;
  readOnly?: boolean;
  clearable?: boolean;
  onFocus?: () => void | null;
  onOpen?: () => void | null;
  onChange?: (updatedDate: DateTime | null) => void | null;
  timezone?: string;
  inputWidth?: number | string;
}

export default function DateInput({
  label,
  name,
  control,
  className = '',
  required = false,
  errors,
  defaultValue = undefined,
  minDate = undefined,
  maxDate = undefined,
  disabled = false,
  readOnly = false,
  clearable = false,
  onFocus = undefined,
  onOpen = undefined,
  onChange = undefined,
  inputWidth = undefined,
  ...otherProps
}: DateInputProps) {
  const { t } = useTranslation('common');
  const requiredFieldMessage = t('common.requiredField');
  const invalidDateMessage = t('common.invalidDate');

  const handleChange = (date: any, changeFn: (val: any) => void) => {
    changeFn(date);
  };
  const validateDate = (date: unknown): boolean | string => {
    const normalized = toLuxonPickerValue(date);
    if (required && !normalized?.isValid) {
      return invalidDateMessage;
    }
    if (!required && normalized && !normalized.isValid) {
      return invalidDateMessage;
    }
    if (minDate && normalized && normalized < minDate) {
      const msg = t('common.dateTooEarly', {
        date: minDate.toLocaleString(DateTime.DATE_SHORT),
      });
      return msg;
    }
    if (maxDate && normalized && normalized > maxDate) {
      const msg = t('common.dateTooLate', {
        date: maxDate.toLocaleString(DateTime.DATE_SHORT),
      });
      return msg;
    }
    return true;
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={{
        required: required && requiredFieldMessage,
        validate: validateDate,
      }}
      render={({ field }: { field: any }) => {
        return (
          <DatePicker
            label={label}
            className={className}
            value={toLuxonPickerValue(field.value)}
            minDate={minDate}
            maxDate={maxDate}
            disabled={disabled}
            readOnly={readOnly}
            slotProps={{
              textField: {
                variant: 'outlined',
                required,
                onFocus: () => {
                  onFocus && onFocus();
                },
                sx: {
                  width: inputWidth || undefined,
                  '& .MuiInputAdornment-root .MuiIconButton-root': {
                    padding: '4px',
                    marginLeft: '-15px',
                  },
                },
              },
              field: {
                clearable: clearable,
              },
            }}
            onChange={(date) => {
              handleChange(date, field.onChange);
              onChange && onChange(date);
            }}
            onOpen={onOpen}
            {...otherProps}
          />
        );
      }}
    />
  );
}
